<template>
    <div style="height: calc(100vh)">
        <ChatWindow
            v-if="activeTab === 0"
            :alwaysScrollToBottom="alwaysScrollToBottom"
            :colors="colors"
            :disableUserListToggle="disableUserListToggle"
            :isOpen="isOpen"
            :messageList="messageList"
            :messageStyling="messageStyling"
            :onClose="close"
            :onUserInputSubmit="onMessageWasSent"
            :participants="participants"
            :placeholder="$t('message.writePlaceHolder')"
            :send-typing="sendTyping"
            :showCloseButton="showCloseButton"
            :showEmoji="showEmoji"
            :showFile="showFile"
            :showLauncher="showLauncher"
            :showTypingIndicator="showTypingIndicator"
            :title="chatWindowTitle"
            :titleImageUrl="titleImageUrl"
            @edit="$emit('edit', $event)"
            @onType="$emit('onType')"
            @remove="$emit('remove', $event)"
            @scrollToTop="$emit('scrollToTop')"
            :chatbot-active="chatbotActive"
        >
            <template v-slot:header>
                <slot name="header"> </slot>
            </template>
            <template v-slot:user-avatar="scopedProps">
                <slot :message="scopedProps.message" :user="scopedProps.user" name="user-avatar"> </slot>
            </template>
            <template v-slot:text-message-body="scopedProps">
                <slot
                    :me="scopedProps.me"
                    :message="scopedProps.message"
                    :messageColors="scopedProps.messageColors"
                    :messageText="scopedProps.messageText"
                    name="text-message-body"
                >
                </slot>
            </template>
            <template v-slot:system-message-body="scopedProps">
                <slot :message="scopedProps.message" name="system-message-body"> </slot>
            </template>
            <template v-slot:text-message-toolbox="scopedProps">
                <slot :me="scopedProps.me" :message="scopedProps.message" name="text-message-toolbox"> </slot>
            </template>
        </ChatWindow>
        <Catalog
            v-if="activeTab === 1"
            :colors="colors"
            :disable-user-list-toggle="disableUserListToggle"
            :on-close="close"
            :participants="participants"
            :show-close-button="showCloseButton"
            :title-image-url="titleImageUrl"
        >
        </Catalog>
    </div>
</template>

<script>
import ChatWindow from "./../popup-chat/ChatWindow.vue";

import CloseIcon from "./assets/close-icon.png";
import OpenIcon from "./assets/logo-no-bg.svg";
import Catalog from "../Catalog.vue";
import { mapActions, mapState } from "vuex";
import Footer from "../Footer";
import { log2 } from "../../../utils/log2";
import * as _ from "lodash";
import { siteIdResolver } from "../../../utils/site";

const log = log2("Launcher");
export default {
    components: {
        ChatWindow,
        Footer,
        Catalog,
    },

    data() {
        return {
            activeTab: 0,
            catalogModeOn: true,
        };
    },
    props: {
        sendTyping: {
            type: Function,
            default: () => {},
        },
        icons: {
            type: Object,
            required: false,
            default: function () {
                return {
                    open: {
                        img: OpenIcon,
                        name: "default",
                    },
                    close: {
                        img: CloseIcon,
                        name: "default",
                    },
                };
            },
        },
        showEmoji: {
            type: Boolean,
            default: false,
        },
        isOpen: {
            type: Boolean,
            required: true,
        },
        open: {
            type: Function,
            required: true,
        },
        close: {
            type: Function,
            required: true,
        },
        showFile: {
            type: Boolean,
            default: false,
        },
        showLauncher: {
            type: Boolean,
            default: true,
        },
        showCloseButton: {
            type: Boolean,
            default: true,
        },
        participants: {
            type: Array,
            default: () => [],
        },
        title: {
            type: String,
            default: () => "",
        },
        titleImageUrl: {
            type: String,
            default: () => "",
        },
        onMessageWasSent: {
            type: Function,
            required: true,
        },
        messageList: {
            type: Array,
            default: () => [],
        },
        newMessagesCount: {
            type: Number,
            default: () => 0,
        },
        placeholder: {
            type: String,
            default: "Write a message...",
        },
        showTypingIndicator: {
            type: String,
            default: () => "",
        },
        colors: {
            type: Object,
            required: false,
            validator: (c) =>
                "header" in c &&
                "bg" in c.header &&
                "text" in c.header &&
                "launcher" in c &&
                "bg" in c.launcher &&
                "messageList" in c &&
                "bg" in c.messageList &&
                "sentMessage" in c &&
                "bg" in c.sentMessage &&
                "text" in c.sentMessage &&
                "receivedMessage" in c &&
                "bg" in c.receivedMessage &&
                "text" in c.receivedMessage &&
                "userInput" in c &&
                "bg" in c.userInput &&
                "text" in c.userInput,
            default: function () {
                return {
                    header: {
                        bg: "#4e8cff",
                        text: "#ffffff",
                    },
                    launcher: {
                        bg: "#4e8cff",
                    },
                    messageList: {
                        bg: "#ffffff",
                    },
                    sentMessage: {
                        bg: "#4e8cff",
                        text: "#ffffff",
                    },
                    receivedMessage: {
                        bg: "#f4f7f9",
                        text: "#ffffff",
                    },
                    userInput: {
                        bg: "#f4f7f9",
                        text: "#565867",
                    },
                };
            },
        },
        alwaysScrollToBottom: {
            type: Boolean,
            default: () => false,
        },
        messageStyling: {
            type: Boolean,
            default: () => false,
        },
        disableUserListToggle: {
            type: Boolean,
            default: false,
        },
        chatbotActive: {
            type: Boolean,
            default: () => false,
        },
    },
    methods: {
        ...mapActions({
            getMainCategories: "catalog/getCategories",
        }),
        openAndFocus() {
            this.open();
            this.$root.$emit("focusUserInput");
        },
        activeTabChanged(active) {
            this.activeTab = active;
        },
    },
    computed: {
        ...mapState({
            connected: (state) => state.chat.connected,
            queueNumber: (state) => state.chat.queueNumber,
            isCatalogOpen: (state) => state.setting.settings.catalogVisible,
            catalog: (state) => state.catalog.content,
            settings: (state) => state.setting.settings,
        }),
        chatWindowTitle() {
            if (this.participants.length === 0) {
                if (this.settings.queueNumberVisible && this.queueNumber) {
                    return this.$t("message.queueNumberDesc", { number: this.queueNumber });
                } else {
                    return this.$t("message.waitRepresentative");
                }
            } else {
                /*return `${this.$t('message.you')}, ` + this.participants[0].name*/
                if (this.participants.length === 1) {
                    return _.startCase(this.participants[0].name.toLowerCase());
                } else if (this.participants.length === 2) {
                    return this.participants
                        .map((x) => _.startCase(x.name.toLowerCase()))
                        .join(` ${this.$t("message.and")} `);
                } else {
                    const allNames = this.participants.map((x) => x.name);

                    return _.take(allNames, allNames.length - 1)
                        .map((x) => _.startCase(x.toLowerCase()) + ", ")
                        .join("")
                        .concat(` ${this.$t("message.and")} ${_.startCase(_.last(allNames).toLowerCase())}`);
                }
            }
        },
    },
    async mounted() {
        /*this.messageList.forEach(x => x.liked = false);*/
        //we can control catalogs open or close status from here
        log("Launcher mounted", {
            msgList: this.messageList,
            catalogOpen: this.isCatalogOpen,
        });
        await siteIdResolver();
        if (this.isCatalogOpen) {
            this.getMainCategories().then((_) => {
                if (this.catalog && this.catalog.length > 0) {
                    this.catalogModeOn = true;
                }
            });
        }
    },
    watch: {
        messageList: {
            handler: function (next, prev) {
                // check someData and eventually call
                log("watch.messageList", next, prev);
                this.messageList = next;
            },
            immediate: true,
        },
        isCatalogOpen(newValue) {
            if (!newValue && this.activeTab === 1) {
                this.activeTab = 0;
            }
        },
    },
    beforeDestroy() {},
};
</script>

<style scoped>
.sc-launcher {
    width: 60px;
    height: 60px;
    background-position: center;
    background-repeat: no-repeat;
    position: fixed;
    right: 25px;
    bottom: 25px;
    border-radius: 50%;
    box-shadow: none;
    transition: box-shadow 0.2s ease-in-out;
    cursor: pointer;
    display: flex;
    justify-content: center;
    vertical-align: middle;
    align-items: center;
}

/*.sc-launcher:before {
    content: '';
    position: relative;
    display: block;
    width: 60px;
    height: 60px;
    border-radius: 50%;
    transition: box-shadow 0.2s ease-in-out;
}*/

.sc-launcher .sc-open-icon,
.sc-launcher .sc-closed-icon {
    width: 60px;
    height: 60px;
    position: fixed;
    right: 25px;
    bottom: 25px;
    transition: opacity 100ms ease-in-out, transform 100ms ease-in-out;
}

.sc-launcher .sc-closed-icon {
    transition: opacity 100ms ease-in-out, transform 100ms ease-in-out;
    width: 60px;
    height: 60px;
}

.sc-launcher .sc-open-icon {
    padding: 20px;
    box-sizing: border-box;
    opacity: 1;
}

.sc-launcher.opened .sc-open-icon {
    transform: rotate(-90deg);
    opacity: 1;
}

.sc-launcher.opened .sc-closed-icon {
    transform: rotate(-90deg);
    opacity: 1;
}

.sc-launcher.opened:before {
    box-shadow: 0px 0px 400px 250px rgba(148, 149, 150, 0.2);
}

.sc-launcher:hover {
    box-shadow: 0 0px 27px 1.5px rgba(0, 0, 0, 0.2);
}

.sc-new-messsages-count {
    position: absolute;
    top: -3px;
    left: 41px;
    display: flex;
    justify-content: center;
    flex-direction: column;
    border-radius: 50%;
    width: 22px;
    height: 22px;
    background: #ff4646;
    color: white;
    text-align: center;
    margin: auto;
    font-size: 12px;
    font-weight: 500;
}

.activeTabs {
    color: #fff;
    background-color: #5a6268;
    border-color: #545b62;
    box-shadow: 0 0 0 0.2rem rgba(130, 138, 145, 0.5);
}
</style>



// WEBPACK FOOTER //
// src/components/V2/popup-chat/Launcher.vue